// ✅ Trekkin.app — NotifyToast.jsx v2.1.0 (BUILD FIX + ANTI #31)
// 🔹 Sistema global de toasts Trekkin (estilo 3 — exclusivo Trekkin)
// 🔹 API compatível:
//    • showToast("msg")
//    • showToast("msg", "type")
//    • showToast({ message, type })
// 🔹 Nunca renderiza objetos (FIX React #31)
// 🔹 Avisos centrados no ecrã, com blur leve e sombra suave

import { useEffect, useState } from "react";
import { useLanguageStore } from "../stores/useLanguageStore";
import "./NotifyToast.css";

// ---- Estado global em memória ----

let listeners = [];
let queue = [];
let counter = 0;

function notifyListeners() {
  listeners.forEach((fn) => fn(queue));
}

function dismissToast(id) {
  queue = queue.filter((item) => item.id !== id);
  notifyListeners();
}

// ---- Helpers de segurança ----

function safeMessage(input) {
  if (input == null) return "";
  if (typeof input === "string") return input;
  if (typeof input === "number") return String(input);
  if (typeof input === "boolean") return input ? "true" : "false";
  if (typeof input === "object") {
    if (typeof input.message === "string") return input.message;
    try {
      return JSON.stringify(input);
    } catch {
      return "";
    }
  }
  return "";
}

function safeType(input) {
  if (["success", "error", "warning", "info"].includes(input)) return input;
  if (input && typeof input === "object" && typeof input.type === "string")
    return ["success", "error", "warning", "info"].includes(input.type)
      ? input.type
      : "info";
  return "info";
}

const STATUS_PREFIX_RE = /^\s*(?:✅|✔️?|☑️|❌|✖️?|⚠️?|ℹ️?|⏳|🗑️?|🔔|🌦️?)\s*/u;

function inferTypeFromMessage(message) {
  const raw = safeMessage(message).trim();
  if (/^(?:✅|✔️?|☑️)/u.test(raw)) return "success";
  if (/^(?:❌|✖️?)/u.test(raw)) return "error";
  if (/^⚠️?/u.test(raw)) return "warning";
  return "info";
}

function normalizeToastMessage(input) {
  return safeMessage(input).replace(STATUS_PREFIX_RE, "").trim();
}

// ---- API pública ----

export function showToast(message, type, duration) {
  if (!message) return;

  const objectType =
    message && typeof message === "object" ? message.type : undefined;
  const resolvedType = safeType(type ?? objectType ?? inferTypeFromMessage(message));
  const normalizedMessage = normalizeToastMessage(message);
  if (!normalizedMessage) return;
  const displayDuration = Number.isFinite(duration)
    ? Math.max(1500, duration)
    : Math.min(8000, Math.max(3000, 1600 + normalizedMessage.length * 35));

  // Do not stack the same notification when two listeners finish the same
  // action at nearly the same time. It may be shown again after it disappears.
  if (
    queue.some(
      (item) =>
        item.message === normalizedMessage && item.type === resolvedType
    )
  ) {
    return;
  }

  // 🔒 NORMALIZAÇÃO TOTAL
  const toast = {
    id: ++counter,
    message: normalizedMessage,
    type: resolvedType,
    createdAt: Date.now(),
  };

  // Keep the notification area compact and visually predictable on phones.
  queue = [...queue, toast].slice(-4);
  notifyListeners();

  // Auto-remove
  setTimeout(() => {
    queue = queue.filter((t) => t.id !== toast.id);
    notifyListeners();
  }, displayDuration);
}

// ---- Componente visual (montar em main.jsx) ----

export function ToastContainer() {
  const [items, setItems] = useState(queue);
  const language = useLanguageStore((state) => state.language);

  useEffect(() => {
    const listener = (q) => setItems([...q]);
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  if (!items.length) return null;

  const labels = language === "en"
    ? { success: "Success", error: "Error", warning: "Attention", info: "Information" }
    : { success: "Sucesso", error: "Erro", warning: "Atenção", info: "Informação" };

  const getIcon = (type) => {
    const paths = {
      success: <path d="m5 12 4 4L19 6" />,
      error: <><path d="M12 7v6" /><path d="M12 17h.01" /><circle cx="12" cy="12" r="9" /></>,
      warning: <><path d="M12 8v5" /><path d="M12 17h.01" /><path d="M10.3 4.7 2.5 18.2A1.5 1.5 0 0 0 3.8 20h16.4a1.5 1.5 0 0 0 1.3-1.8L13.7 4.7a1.5 1.5 0 0 0-3.4 0Z" /></>,
      info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" /></>,
    };
    return <svg viewBox="0 0 24 24" width="23" height="23" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[type] || paths.info}</svg>;
  };

  return (
    <div className="tk-toast-stack">
      {items.map((t) => {
        return (
          <div
            key={t.id}
            className={`tk-toast tk-toast--${t.type}`}
            role={t.type === "error" || t.type === "warning" ? "alert" : "status"}
          >
            <div className="tk-toast-icon">
              {getIcon(t.type)}
            </div>
            <div className="tk-toast-content">
              <div className="tk-toast-title">{labels[t.type] || labels.info}</div>
              <div className="tk-toast-message">{t.message}</div>
            </div>
            <button
              type="button"
              className="tk-toast-close"
              onClick={() => dismissToast(t.id)}
              aria-label={language === "en" ? "Dismiss notification" : "Fechar aviso"}
            >×</button>
          </div>
        );
      })}
    </div>
  );
}
